import { computed, Injectable } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { DataUtils } from '@iupics-util/tools/data.utils';
import { BehaviorSubject, timer } from 'rxjs';

@Injectable()
export class StudioMessageService {
  private messages$ = new BehaviorSubject<Message[]>([]);

  messages = toSignal(this.messages$.asObservable());

  message = computed(() => this.messages()?.[0]);

  private readonly defaultDuration = 8000;

  addMessage(title: string, text?: string, type: Message['type'] = 'info', duration?: number): string {
    const id = DataUtils.uuid();
    const message: Message = {
      id,
      title,
      text,
      type,
      duration: duration || this.defaultDuration,
    };

    const currentMessages = this.messages$.value;
    this.messages$.next([...currentMessages, message]);

    this.scheduleRemoval(id, message.duration);

    return id;
  }

  removeMessage(id: string): void {
    const currentMessages = this.messages$.value;
    const filteredMessages = currentMessages.filter((msg) => msg.id !== id);
    this.messages$.next(filteredMessages);
  }

  clearAllMessages(): void {
    this.messages$.next([]);
  }

  success(title: string, text?: string, duration?: number): string {
    return this.addMessage(title, text, 'success', duration);
  }

  error(title: string, text?: string, duration?: number): string {
    return this.addMessage(title, text, 'alert', duration);
  }

  warning(title: string, text?: string, duration?: number): string {
    return this.addMessage(title, text, 'warning', duration);
  }

  info(title: string, text?: string, duration?: number): string {
    return this.addMessage(title, text, 'info', duration);
  }

  private scheduleRemoval(id: string, duration: number): void {
    timer(duration).subscribe(() => {
      this.removeMessage(id);
    });
  }
}

export type Message = {
  id: string;
  title: string;
  text?: string;
  type: 'success' | 'alert' | 'warning' | 'info';
  duration?: number;
};
